// Accessibility props for table rows that expand to reveal a detail row.
//
// Across the dashboards these rows were plain `<tr onClick={...}>`. A bare tr with
// a click handler is not focusable and announces nothing, so the drill-down content
// (city breakdowns, hourly splits, per-page metrics) was unreachable without a
// mouse — and in most of these tables the row click is the ONLY way to reach it.
//
// Returns the props to spread onto the <tr>. The visual classes stay at the call
// site because each table styles its own rows; only the focus ring is appended,
// since a focusable element with no visible focus indicator is its own failure.
//
// Usage:
//   <tr {...expandableRowProps({
//         expanded: isOpen,
//         onToggle: () => setOpen(!isOpen),
//         label: `details for ${row.channel}`,
//         className: 'border-b hover:bg-gray-50 ...',
//       })}>
//
// Note on semantics: these rows toggle a sibling detail row rather than navigating,
// so role="button" + aria-expanded is correct. Enter and Space both activate, and
// Space is preventDefault-ed so it toggles instead of scrolling the page.
export const FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-inset';

export const expandableRowProps = ({ expanded, onToggle, label, className = '' }) => ({
  onClick: onToggle,
  onKeyDown: (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  },
  role: 'button',
  tabIndex: 0,
  'aria-expanded': expanded,
  'aria-label': `${expanded ? 'Collapse' : 'Expand'} ${label}`,
  className: `${className} ${FOCUS_RING}`.trim(),
});

export default expandableRowProps;
